import {
  useReducer,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { initialState } from "./Customers.model";
import { CUSTOMERS_ACTIONS } from "./Customers.actions";
import { CustomersReducer } from "./Customers.reducers";
import { CustomersServices } from "./Customers.services";
import GROUPS_CONSTANTS, {
  DEFAULT_CUSTOMERS_LIST_FILTERS,
  GROUPS_FACILITIES_PICKER,
  GROUPS_LIST_PAGINATION,
  normalizeCustomersListFilters,
} from "./Customers.constants";

export const useCustomersViewInitialization = () => {
  const [state, dispatch] = useReducer(CustomersReducer, initialState);
  const services = CustomersServices();
  const paginationRef = useRef(state.pagination);
  paginationRef.current = state.pagination;
  const filtersRef = useRef(state.listFilters);
  filtersRef.current = state.listFilters;
  const actions = CustomersActions(dispatch, services, paginationRef, filtersRef);

  useEffect(() => {
    actions.getAllCustomers(0, GROUPS_LIST_PAGINATION.DEFAULT_PAGE_SIZE);
  }, []);

  return { state, actions };
};

const CustomersActions = (dispatch, services, paginationRef, filtersRef) => {
  const getAllCustomers = async (
    page = paginationRef.current.page,
    size = paginationRef.current.size,
    filters = filtersRef.current,
    manageLoading = true
  ) => {
    const listFilters = { ...DEFAULT_CUSTOMERS_LIST_FILTERS, ...filters };
    dispatch({
      type: CUSTOMERS_ACTIONS.LIST_FILTERS,
      payload: listFilters,
    });
    if (manageLoading) {
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: true,
      });
    }
    try {
      const response = await services.getAllCustomers({
        page,
        size,
        ...listFilters,
      });
      if (response?.error) {
        dispatch({
          type: CUSTOMERS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
        return;
      }
      const sortedList =
        response?.data?.data?.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
      const apiPagination = response?.data?.pagination ?? {};
      dispatch({
        type: CUSTOMERS_ACTIONS.CUSTOMERSLIST,
        payload: {
          list: sortedList,
          pagination: {
            page: Number(apiPagination.pageIndex ?? page),
            size: Number(apiPagination.pageSize ?? size),
            totalCount: Number(apiPagination.totalCount ?? 0),
            totalPages: Number(apiPagination.totalPages ?? 0),
            hasNext: Boolean(apiPagination.hasNext),
            hasPrevious: Boolean(apiPagination.hasPrevious),
          },
        },
      });
    } catch {
      dispatch({
        type: CUSTOMERS_ACTIONS.ERROR,
        payload: "Unable to load customers list",
      });
    } finally {
      if (manageLoading) {
        dispatch({
          type: CUSTOMERS_ACTIONS.LOADING,
          payload: false,
        });
      }
    }
  };

  const actions = {
    getAllCustomers,
    onPageChange: async (nextPage) => {
      await getAllCustomers(nextPage, paginationRef.current.size);
    },
    onRowsPerPageChange: async (size) => {
      await getAllCustomers(0, size);
    },
    onListFiltersChange: async (rawColumnFilters) => {
      const listFilters = normalizeCustomersListFilters(rawColumnFilters);
      await getAllCustomers(0, paginationRef.current.size, listFilters);
    },
    changeViewStatus: (status, selectedCustomer) => {
      dispatch({
        type: CUSTOMERS_ACTIONS.SELECTEDVIEW,
        payload: { status: status, customer: selectedCustomer },
      });
    },
    addNewCustomer: async (customerInfo, onSuccessCallBack) => {
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.addNewCustomer(customerInfo);
      if (response?.error) {
        dispatch({
          type: CUSTOMERS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        onSuccessCallBack(response.data);
      }
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: false,
      });
    },
    editCustomerInfo: async (customerInfo, status, onSuccessCallBack) => {
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.editCustomer(customerInfo);
      if (response?.error) {
        dispatch({
          type: CUSTOMERS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        if (response.data) {
          onSuccessCallBack(response.data);
        }
        dispatch({
          type: CUSTOMERS_ACTIONS.SELECTEDVIEW,
          payload: { status: status, customer: null },
        });
      }
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: false,
      });
    },
    patchCustomerInfo: async (customerInfo, code, status, onSuccessCallBack) => {
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: true,
      });
      const response = await services.patchCustomer(customerInfo, code);
      if (response?.error) {
        dispatch({
          type: CUSTOMERS_ACTIONS.ERROR,
          payload: response?.error ?? GROUPS_CONSTANTS.toast.MESSAGE_FAILURE,
        });
      } else {
        if (response.status === 204) {
          await onSuccessCallBack?.(response.data);
        }
        dispatch({
          type: CUSTOMERS_ACTIONS.SELECTEDVIEW,
          payload: { status: status, customer: null },
        });
      }
      dispatch({
        type: CUSTOMERS_ACTIONS.LOADING,
        payload: false,
      });
    },
    refreshError: () => {
      dispatch({
        type: CUSTOMERS_ACTIONS.ERROR,
        payload: null,
      });
    },
    searchCustomers: (searchString) => {
      dispatch({
        type: CUSTOMERS_ACTIONS.SEARCHCUSTOMER,
        payload: searchString,
      });
    },
  };
  return actions;
};

function readFacilitiesPage(response) {
  if (response?.error || (response?.status !== 200 && response?.status !== 201)) {
    return { items: [], pagination: null };
  }
  const body = response?.data;
  const items = Array.isArray(body?.data) ? body.data : [];
  return { items, pagination: body?.pagination ?? null };
}

export const useHachwimsFacilityOptions = (selectedHachwims, facilitiesFetchKey) => {
  const customersServices = useMemo(() => CustomersServices(), []);

  const [filterText, setFilterText] = useState("");
  const [items, setItems] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const requestIdRef = useRef(0);
  const nextPageRef = useRef(0);
  const debounceRef = useRef(null);

  const beginNewSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1;
    nextPageRef.current = 0;
    setHasNextPage(true);
    setItems([]);
  }, []);

  const fetchPage = useCallback(
    async (page, append, text) => {
      const id = requestIdRef.current;
      if (append) setLoadingMore(true);
      else setLoadingInitial(true);
      try {
        const res = await customersServices.getHachwimsFacilityOptions({
          text,
          page,
          size: GROUPS_FACILITIES_PICKER.PAGE_SIZE,
        });
        if (id !== requestIdRef.current) return;

        const { items: pageItems, pagination } = readFacilitiesPage(res);
        setItems((prev) => (append ? [...prev, ...pageItems] : [...pageItems]));

        const pageIndex = pagination?.pageIndex ?? page;
        setHasNextPage(
          Boolean(pagination?.hasNext ?? pageItems.length >= GROUPS_FACILITIES_PICKER.PAGE_SIZE)
        );
        nextPageRef.current = pageIndex + 1;
      } catch {
        if (id === requestIdRef.current) {
          if (!append) setItems([]);
          setHasNextPage(false);
        }
      } finally {
        setLoadingInitial(false);
        setLoadingMore(false);
      }
    },
    [customersServices]
  );

  useEffect(() => {
    beginNewSearch();
    setItems([]);
  }, [facilitiesFetchKey, beginNewSearch]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const onFacilitiesDropdownOpen = useCallback(() => {
    beginNewSearch();
    // Always load the unfiltered first page on open. Search text is cleared on close /
    // Autocomplete reset, so using a stale filterText here left only the last search hits.
    fetchPage(0, false, "");
  }, [beginNewSearch, fetchPage]);

  const onFacilitiesDropdownClose = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setFilterText("");
    beginNewSearch();
  }, [beginNewSearch]);

  const loadNextPage = useCallback(() => {
    if (!hasNextPage || loadingInitial || loadingMore) return;
    fetchPage(nextPageRef.current, true, filterText);
  }, [fetchPage, filterText, hasNextPage, loadingInitial, loadingMore]);

  const onFacilityListboxScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - GROUPS_FACILITIES_PICKER.LISTBOX_SCROLL_THRESHOLD_PX) return;
      loadNextPage();
    },
    [loadNextPage]
  );

  const onFacilityInputChange = useCallback(
    (_, value, reason) => {
      // Autocomplete clears the input on blur / after select (reason "reset"). Sync search
      // state and reload the full list so the dropdown is not stuck on the last query.
      if (reason === "reset") {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        setFilterText("");
        beginNewSearch();
        fetchPage(0, false, "");
        return;
      }
      if (reason !== "input" && reason !== "clear") return;
      setFilterText(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        beginNewSearch();
        fetchPage(0, false, value);
      }, GROUPS_FACILITIES_PICKER.SEARCH_DEBOUNCE_MS);
    },
    [beginNewSearch, fetchPage]
  );

  const hachwimsDropdownOptions = useMemo(() => {
    const out = [...items];
    const selected = Array.isArray(selectedHachwims) ? selectedHachwims : [];
    for (const s of selected) {
      if (s?.id && !out.some((o) => o.id === s.id)) out.push(s);
    }
    return out;
  }, [items, selectedHachwims]);

  return {
    hachwimsDropdownOptions,
    loadingHachwimsOptions: loadingInitial || loadingMore,
    onFacilityInputChange,
    onFacilityListboxScroll,
    onFacilitiesDropdownOpen,
    onFacilitiesDropdownClose,
  };
};
